import AlertPopup from '../ui/AlertPopup';
import Disaster from './Disaster';
import common from '../common';
import { AdjustmentFilter } from 'pixi-filters';
import ParticleEmitter from '../ParticleEmitter';

export default class Hurricane extends Disaster {
	constructor(world) {
		super(world);

		this._rainTextures = [
			'droplet_01',
			'droplet_02',
			'droplet_03',
			'droplet_04',
			'droplet_05',
			'droplet_06',
			'droplet_07',
		];

		this._canUpdate = true;
		this._maxDroplets = 300;
		this._lastDropletSpawned = Date.now();

		this.minX = (P3.View.width - common.STAGE_WIDTH) * 0.5;
		this.maxX = (common.STAGE_WIDTH + P3.View.width) * 0.5;
		this.minY = 0;
		this.maxY = common.STAGE_HEIGHT;

		this._rainEmitter = null;

		this._adfilter = new AdjustmentFilter();
		common.world.filterArea = new PIXI.Rectangle(0, 0, P3.View.width, P3.View.height);
		common.world.filters = [this._adfilter];

		this._palmtrees = [];
		this._destructibleTiles = [];
		this._canRain = false;
		this._lastStructureDestroyed = Date.now();
		this._destroyCooldown = 111;
		this._strikeLast = Date.now();
		this._ambientSound = P3.audio.playSound('sfx_hr_ambience_loop_00', { loop: true });

		this._darkening = false;
		this._rainCooldown = { c: 2000 };
		this._destroyed = false;
		this._stormTweens = new TWEEN.Group();

		this._droplets = new PIXI.ParticleContainer();
		this._world.addChild(this._droplets);
	}

	destroy() {
		this._canUpdate = false;
		this._destroyed = true;
		this._droplets.destroy({ children: true });

		if (this._rainEmitter) {
			this._rainEmitter.stop();
			this._rainEmitter.visible = false;
			this._rainEmitter.destroy({ children: true });
		}

		super.destroy();
	}

	update(dt) {
		if (!this._canUpdate) {
			return;
		}

		super.update(dt);

		this._stormTweens.update();

		if (this._droplets) {
			for (const drop of this._droplets.children) {
				drop.y += drop.speed * window.dt;
			}
		}

		if (this._rainEmitter) {
			this._rainEmitter.update();
		}

		if (this._canRain) {
			this.rain();
		}

		if (!this._isRunning) {
			return;
		}

		if (Date.now() - this._lastStructureDestroyed > this._destroyCooldown) {
			this._lastStructureDestroyed = Date.now();
			this.destroyStructure();
		}
	}

	destroyStructure() {
		if (this._destructibleTiles.length <= 0) {
			this.end();

			return;
		}

		const tileIndex = P3.math.randomInt(0, this._destructibleTiles.length - 1);
		const tile = this._destructibleTiles[tileIndex];

		const strength = tile.strength + (tile.defenceEffect * 20);

		let type;
		let dead;
		let injured;
		let survive;
		let cost;
		const structure = tile.data.structure;

		if (structure.population > 0) {
			if (Math.random() > strength / 100) { // total destruction
				tile.damaged = true;
				tile.setStructureTexture();
				type = AlertPopup.TYPE_DEAD;
				dead = structure.population * 0.85;
				cost = structure.cost * 1.75;
				dead = Math.random() > 0.5 ? Math.ceil(dead) : Math.floor(dead);
				injured = Math.max(0, structure.population - dead);
				survive = 0;
				this._totalDestroyed++;

				this._hospitalDestroyed = structure.special && structure.special === 'hospital';
				this._schoolDestroyed = structure.special && structure.special === 'school';
			} else if (Math.random() > strength / 150) {
				type = AlertPopup.TYPE_INJURED;
				dead = 0;
				cost = Number(structure.cost);
				injured = structure.population;
				survive = 0;
			} else {
				type = AlertPopup.TYPE_SURVIVE;
				dead = 0;
				cost = 0;
				injured = 0;
				survive = structure.population;
			}
			this._totalDeaths += dead;
			this._totalInjury += injured;
			this._totalSurvive += survive;
			this._totalCost += cost;
			tile.parent && tile.alert.start(type);
		} else if (Math.random() > strength / 100) { // total destruction
			tile.damaged = true;
			tile.setStructureTexture();
		}
		this._destructibleTiles.splice(tileIndex, 1);
	}

	rain() {
		if (this._droplets.children.length >= 500) {
			return;
		}
		if (Date.now() - this._lastDropletSpawned > this._rainCooldown.c) {
			this._lastDropletSpawned = Date.now();
			this._droplets.addChild(this._createDroplet());
		}
	}

	strikeLightning() {
		new TWEEN.Tween(this._adfilter, this._tweens)
			.to({
				brightness: 4,
				gamma: 1.3,
				blue: 1.2,
				red: 1.1,
			}, 150)
			.easing(TWEEN.Easing.Linear.None)
			.repeat(1)
			.yoyo(true)
			.start();
	}

	_createDroplet() {
		let drop = new PIXI.Sprite(P3.assets.texture(P3.utils.selectRandom(this._rainTextures)));

		drop.anchor.set(0.5);
		drop.speed = P3.math.randomInt(5, 60);
		drop.position.set(P3.math.randomInt(this.minX, this.maxX), P3.math.randomInt(this.minY, this.maxY));

		const fadeTween = new TWEEN.Tween(drop, this._tweens)
			.to({
				alpha: 0,
			}, 1000)
			.easing(TWEEN.Easing.Linear.None)
			.onComplete(() => {
				drop.destroy();
				drop = null;
			});

		new TWEEN.Tween({}, this._tweens)
			.to({
			}, 5000)
			.easing(TWEEN.Easing.Linear.None)
			.onComplete(() => {
				fadeTween.start();
			}, this)
			.start();

		return drop;
	}

	start() {
		if (super.start()) {
			this.darken(4000, true);

			P3.utils.delayg(this._tweens, 1.2, () => {
				P3.audio.playSound('sfx_hr_disaster_strikes_00');
			});
			P3.utils.delayg(this._tweens, 4, () => {
				this.strikeLightning();
				P3.utils.delayg(this._tweens, 4, () => {
					this.strikeLightning();
					P3.utils.delayg(this._tweens, 0.3, () => {
						this.strikeLightning();
					});
				});
			});

			for (let y = 0, rows = this._world.rows; y < rows; ++y) {
				for (let x = 0, columns = this._world.columns; x < columns; ++x) {
					const tile = this._world.getTile(x, y);

					if (tile.up && tile.up.data.structure && tile.up.data.structure.type === 0) {
						tile.defenceEffect++;
					}
					if (tile.down && tile.down.data.structure && tile.down.data.structure.type === 0) {
						tile.defenceEffect++;
					}
					if (tile.left && tile.left.data.structure && tile.left.data.structure.type === 0) {
						tile.defenceEffect++;
					}
					if (tile.right && tile.right.data.structure && tile.right.data.structure.type === 0) {
						tile.defenceEffect++;
					}

					if (tile.isWater) {
						tile.waveAmplitude = 10;
						tile.waveFrequency = 4;
					}
					if (tile.data.structure && tile.data.structure.group === 50) {
						tile.playWindAnim();
					}
					if (tile.data.structure && tile.data.structure.images.damage) {
						if (tile.data.structure.id === 240) {
							continue;
						} // prevent Community Centre from being destroyed
						this._destructibleTiles.push(tile);
					}
				}
			}
		}
	}

	darken(t = 20000, override = false) {
		if (this._darkening && !override) {
			return;
		}

		if (override) {
			this._stormTweens.removeAll();
		}

		this._darkening = true;

		this._rainEmitter = new ParticleEmitter('particle_rain_angle_emitter', [
			P3.assets.texture('particle_raindrop_00'),
		], true);
		this._rainEmitter.alpha = 0;
		this._rainEmitter.enableLoop();
		this._rainEmitter.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this._world.addChild(this._rainEmitter);
		this._rainEmitter.play();

		this.resize();

		new TWEEN.Tween(this._rainCooldown, this._stormTweens)
			.to({ c: 10 }, t * 0.5)
			.easing(TWEEN.Easing.Linear.None)
			.start();

		new TWEEN.Tween(this._rainEmitter, this._stormTweens)
			.to({ alpha: 1 }, t)
			.easing(TWEEN.Easing.Linear.None)
			.start();

		new TWEEN.Tween(this._adfilter, this._stormTweens)
			.to({
				saturation: 0.4,
				contrast: 1.3,
				brightness: 0.7,
			}, t)
			.easing(TWEEN.Easing.Linear.None)
			.onStart(() => {
				this._canRain = true;
			})
			.start();
	}

	stopHurricane() {
		this._canRain = false;
		new TWEEN.Tween(this._adfilter, this._stormTweens)
			.to({
				saturation: 1,
				contrast: 1,
				brightness: 1,
			}, 4000)
			.easing(TWEEN.Easing.Linear.None)
			.onComplete(() => {
				common.world.filters = null;
			})
			.start();

		if (this._rainEmitter) {
			new TWEEN.Tween(this._rainEmitter, this._stormTweens)
				.to({ alpha: 0 }, 4000)
				.easing(TWEEN.Easing.Linear.None)
				.onComplete(() => {
					this._rainEmitter.stop();
					this._rainEmitter.destroy({ children: true });
				})
				.start();
		}

		for (let y = 0, rows = this._world.rows; y < rows; ++y) {
			for (let x = 0, columns = this._world.columns; x < columns; ++x) {
				const tile = this._world.getTile(x, y);

				if (tile.structure === -99) {
					tile.stopWindAnim();
					continue;
				}
				if (tile.isWater) {
					tile.waveAmplitude = 4;
					tile.waveFrequency = 2.4;
				}
			}
		}
	}

	end() {
		if (!this._isRunning) {
			return;
		}
		this.stopHurricane();

		const unhoused = common.scenario.population.unhoused;

		this._totalDeaths += P3.math.randomInt(0, unhoused); // kills a random amount of unhoused civs
		this._totalDeaths *= Math.max(1 - (common.scenario._ccUpgrades.length / 10), 0.5);
		this._totalDeaths = Math.floor(this._totalDeaths);
		this._totalSurvive = common.scenario.population.total - this._totalDeaths;

		if (!this._destroyed) {
			this.signals.disasterEnd.dispatch(this);
		}
		this._isRunning = false;
		this._canStart = false;
	}

	resize() {
		common.world.filterArea = new PIXI.Rectangle(0, 0, P3.View.width, P3.View.height);

		this.minX = (P3.View.width - common.STAGE_WIDTH) * 0.5;
		this.maxX = (common.STAGE_WIDTH + P3.View.width) * 0.5;
		this.minY = 0;
		this.maxY = common.STAGE_HEIGHT;

		if (this._rainEmitter) {
			this._rainEmitter.width = P3.View.width;
			this._rainEmitter.height = P3.View.height;
		}
	}

	damageStructure(tile) {
		if (tile.damaged) {
			return;
		}
		tile.damaged = true;
	}

	get canRain() {
		return this._canRain;
	}

	set canRain(v) {
		this._canRain = v;
	}
}
